/**
 * RolesManagementPage - Page de gestion des rôles (style Discord).
 *
 * Permet à un super_admin (permission `roles.manage` au scope `global`) de :
 *   - Lister les rôles existants (système + customs)
 *   - Créer / renommer / supprimer un rôle
 *   - Configurer les permissions de chaque rôle (catégorie x scope)
 *   - Gérer les membres ayant un rôle donné
 *
 * Layout :
 *   - Desktop (>= 1024px) : split horizontal — RoleList (~320px) | RoleEditor (flex 1)
 *   - Phone/tablet         : drill-down — la liste prend 100%, la sélection
 *                            ouvre RoleEditor en plein écran avec un bouton Retour
 *
 * Gate d'entrée : permission `roles.manage` au scope `global`. Sinon, écran
 * "Accès refusé" centré (pas de redirection).
 */

import React, { useState } from 'react';
import { useHasPermission } from '../SupabaseWrapper';
import { ShieldIcon } from '../icons';
import { useViewport } from '../hooks/useViewport';
import RoleList from './roles/RoleList';
import RoleEditor from './roles/RoleEditor';

interface RolesManagementPageProps {
  refreshTrigger?: number;
}

function RolesManagementPage({ refreshTrigger = 0 }: RolesManagementPageProps) {
  const canManage = useHasPermission('roles.manage', 'global');
  const { isDesktop } = useViewport();

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  console.log('[RolesPage] Render — canManage:', canManage, 'selectedRoleId:', selectedRoleId, 'reloadKey:', reloadKey);

  // Refetch quand refreshTrigger change (navigation vers cet onglet)
  const isFirstMount = React.useRef(true);
  React.useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    console.log('[RolesPage] refreshTrigger changed, bumping reloadKey');
    setReloadKey(k => k + 1);
  }, [refreshTrigger]);

  // Gate : pas de permission → accès refusé
  if (!canManage) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px',
        padding: '32px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0, textAlign: 'center', maxWidth: '420px' }}>
          La gestion des rôles est réservée aux super-administrateurs.
        </p>
      </div>
    );
  }

  const handleRoleChanged = () => {
    console.log('[RolesPage] role changed, bumping reloadKey');
    setReloadKey(k => k + 1);
  };

  const handleRoleDeleted = () => {
    console.log('[RolesPage] role deleted, clearing selection + bumping reloadKey');
    setSelectedRoleId(null);
    setReloadKey(k => k + 1);
  };

  // Mode mobile : drill-down. Si un rôle est sélectionné, on n'affiche que l'éditeur.
  if (!isDesktop) {
    if (selectedRoleId) {
      return (
        <RoleEditor
          roleId={selectedRoleId}
          onBack={() => setSelectedRoleId(null)}
          onChanged={handleRoleChanged}
          onDeleted={handleRoleDeleted}
        />
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <RoleList
          selectedRoleId={selectedRoleId}
          onSelect={setSelectedRoleId}
          reloadKey={reloadKey}
        />
      </div>
    );
  }

  // Mode desktop : split horizontal
  return (
    <div style={{
      display: 'flex',
      gap: '20px',
      alignItems: 'flex-start',
      minHeight: 'calc(100vh - 200px)'
    }}>
      <div style={{ width: '340px', flexShrink: 0 }}>
        <RoleList
          selectedRoleId={selectedRoleId}
          onSelect={setSelectedRoleId}
          reloadKey={reloadKey}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <RoleEditor
          roleId={selectedRoleId}
          onChanged={handleRoleChanged}
          onDeleted={handleRoleDeleted}
        />
      </div>
    </div>
  );
}

export default RolesManagementPage;
